import React, { useState } from 'react';
import { useAuth } from './hooks/useAuth';
import LoginForm from './components/Auth/LoginForm';
import RegisterForm from './components/Auth/RegisterForm';
import ProfileForm from './components/Profile/ProfileForm';
import { authService } from './services/authService';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Button, 
  Box,
  Container,
  useMediaQuery,
  Tabs,
  Tab,
  ThemeProvider
} from '@mui/material';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import AccountBoxOutlinedIcon from '@mui/icons-material/AccountBoxOutlined';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import MatchRequestsPage from './pages/MatchRequests';
import ProfilesPage from './pages/Profiles';
import MessagesPage from './pages/Messages';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import RequireProfile from './components/Auth/RequireProfile';
import theme from './theme';
import VerificationPage from './components/Verification/VerificationPage';
import LandingPage from './components/Landing/LandingPage';

const App: React.FC = () => {
  const { user } = useAuth();
  

  return (
    <ThemeProvider theme={theme}> 
    <Router>
      <Box sx={{ flexGrow: 1 }}>
        <Routes>
          <Route path="/login" element={
            user && user.emailVerified ? <Navigate to="/dashboard" /> : <LoginForm onToggleForm={() => {}} />
          } />
          <Route path="/register" element={
            user && user.emailVerified ? <Navigate to="/dashboard" /> : <RegisterForm onToggleForm={() => {}} />
          } />
          <Route path="/create-profile" element={
            !user ? <Navigate to="/login" /> : <ProfileForm />
          } />
          <Route path="/verify" element={
            !user ? <Navigate to="/login" /> : <VerificationPage />
          } />
          <Route path="/dashboard" element={
            !user ? <Navigate to="/login" /> : (
              <RequireProfile>
                <MainLayout />
              </RequireProfile>
            )
          } />
          <Route path="/" element={
            user ? <Navigate to="/dashboard" /> : <LandingPage />
          } />
        </Routes>
      </Box>
    </Router>
    </ThemeProvider>
  );
};

const Footer: React.FC = () => {
  return (
    <Box
      component="footer"
      sx={{
        py: 3,
        px: 2,
        mt: 'auto',
        backgroundColor: 'white',
        borderTop: 1,
        borderColor: 'divider'
      }}
    >
      <Container maxWidth="md">
        <Typography variant="body2" color="text.secondary" align="center">
          Contact us at <a href="mailto:info@loveisgreen.xyz">info@loveisgreen.xyz</a>
        </Typography>
      </Container>
    </Box>
  );
};

// Move the main app layout to a separate component
const MainLayout: React.FC = () => {
  const { user } = useAuth();
  const [currentTab, setCurrentTab] = useState(0);
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Add state to track if a match is selected in messages
  const [selectedMatchInMessages, setSelectedMatchInMessages] = useState(false);

  const handleLogout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  // Determine if footer should be shown
  const shouldShowFooter = !(isMobile && currentTab === 2 && selectedMatchInMessages);

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column',
      minHeight: '100vh',
      position: 'relative'
    }}>
      <AppBar position="static" sx={{ backgroundColor: 'white' }}>
        <Toolbar sx={{ padding: '0 16px' }}>
          <img 
            src="/mylogo.png" 
            alt="Love is Green Logo" 
            style={{
              height: isMobile ? '80px' : '100px',
              width: 'auto',
              margin: '8px 0',
            }}
          />
          
          {/* Right: User Controls */}
          <Box sx={{ flexGrow: 1 }} />
          <Box sx={{ 
            display: 'flex', 
            flexDirection: isMobile ? 'row' : 'row',
            alignItems: 'center',
            justifyContent: isMobile ? 'flex-end' : 'flex-start',
            gap: 1,
            width: isMobile ? '100%' : 'auto'
          }}>
            <Typography 
              variant={isMobile ? "body2" : "body1"} 
              sx={{ 
                color: 'primary.main',
                display: isMobile ? 'none' : 'block' // Hide email on mobile
              }}
            >
            </Typography>
            <Button 
              fullWidth = {isMobile ? false : true}
              color="inherit" 
              onClick={() => navigate('/verify')} 
              sx={{ 
                color: user?.verificationStatus === 'verified' ? 'success.main' : 'warning.main',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              {user?.verificationStatus === 'verified' ? <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {isMobile ? "Verified" : "Verified"}<HowToRegIcon />
              </Box> : <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {isMobile ? "" : "Verify US Citizenship"}<HowToRegIcon />
              </Box>}
            </Button>
            <Button 
              fullWidth = {isMobile ? false : true}
              color="inherit" 
              onClick={() => navigate('/create-profile')} 
              sx={{ color: 'primary.main' }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {isMobile ? "" : "Edit Profile"}<AccountBoxOutlinedIcon />
              </Box>
            </Button>
            <Button 
              fullWidth = {isMobile ? false : true}
              color="inherit" 
              onClick={handleLogout} 
              sx={{ color: 'primary.main' }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {isMobile ? "" : "Logout"}<ExitToAppIcon />
              </Box>
            </Button>
          </Box>
        </Toolbar>
        
        <Tabs 
          value={currentTab} 
          onChange={(e, newValue) => setCurrentTab(newValue)}
          centered
          sx={{ 
            bgcolor: 'primary.dark',
            '& .MuiTab-root': {
              minWidth: isMobile ? '33%' : 'auto', // Adjust tab width on mobile
              fontSize: isMobile ? '0.8rem' : '0.875rem' // Smaller font on mobile
            }
          }}
        >
          <Tab label="Profiles" sx={{ color: 'white' }} />
          <Tab label="Match Requests" sx={{ color: 'white' }} />
          <Tab label="Messages" sx={{ color: 'white' }} />
        </Tabs>
      </AppBar>
      
      {/* Main Content Area */}
      <Box sx={{ 
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        pb: shouldShowFooter ? '64px' : 0, // Only add padding if footer is shown
        overflow: 'auto'
      }}> 
        <Container>
          {currentTab === 0 && <ProfilesPage />}
          {currentTab === 1 && <MatchRequestsPage />}
          {currentTab === 2 && (
            <MessagesPage 
              onMatchSelect={(isSelected) => setSelectedMatchInMessages(isSelected)} 
            />
          )}
        </Container>
      </Box>

      {/* Footer */}
      {shouldShowFooter && (
        <Box
          component="footer"
          sx={{
            py: 1,
            px: 2,
            backgroundColor: 'white',
            borderTop: 1,
            borderColor: 'divider',
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '50px',
            zIndex: 1
          }}
        >
          <Container maxWidth="md">
            <Typography variant="body2" color="text.secondary" align="center">
              Contact us at <a href="mailto:info@loveisgreen.xyz">info@loveisgreen.xyz</a>
            </Typography>
          </Container>
        </Box>
      )}
    </Box>
  );
};

export default App;